import {
  ElementNode,
  DecoratorNode,
  Spread,
  SerializedElementNode,
  DOMConversionMap,
  EditorConfig,
  LexicalEditor,
} from 'lexical';
import { NodeKey } from 'lexical';
import { MentionSuggestion, MentionType } from '../../../api/mentions';
import { getRoutePath, Pages } from '../../../router/constants';
import Mention from '../../common/mention';

type SerializedMentionNode = Spread<
  {
    type: 'mention';
    version: 1;
    id: string;
    displayName: string;
    mentionType: MentionType;
  },
  SerializedElementNode
>;

export class MentionNode extends DecoratorNode<JSX.Element> {
  __id: string;
  __displayName: string;
  __mentionType: MentionType;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.__id,
      node.__displayName,
      node.__mentionType,
      node.__key,
    );
  }

  isInline(): boolean {
    return true;
  }

  isAtomic(): boolean {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  canInsertTextInside(): boolean {
    return false;
  }

  canBeEmpty(): boolean {
    return false;
  }

  canMergeWith(node: ElementNode): boolean {
    return false;
  }

  static importJSON(json: SerializedMentionNode): MentionNode {
    return new MentionNode(json.id, json.displayName, json.mentionType);
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (node: HTMLElement) => {
        // Cast to HTMLAnchorElement since we know it's an anchor
        const anchorNode = node as HTMLAnchorElement;

        // Check if this is a mention link by presence of mention class
        const hasMentionClass = anchorNode.className.includes('mention');

        if (!hasMentionClass) {
          return null;
        }

        return {
          conversion: () => {
            const textContent = anchorNode.textContent?.trim() ?? '';
            const id = anchorNode.getAttribute('data-mention-id');
            const type = anchorNode.getAttribute('data-mention-type');

            if (id && type) {
              return {
                node: new MentionNode(id, textContent, type as MentionType),
              };
            }

            return null;
          },
          priority: 2,
        };
      },
    };
  }

  exportJSON(): SerializedMentionNode {
    return {
      type: 'mention',
      version: 1,
      id: this.__id,
      displayName: this.__displayName,
      mentionType: this.__mentionType,
      children: [],
      direction: null,
      format: '',
      indent: 0,
    };
  }

  exportDOM(): {
    element: HTMLAnchorElement;
  } {
    const link = document.createElement('a');
    link.href = resolveMentionHref(this.__mentionType, this.__id);
    link.className = `mention mention-${this.__mentionType.toLowerCase()}`;
    link.setAttribute('data-mention-id', this.__id);
    link.setAttribute('data-mention-type', this.__mentionType);
    link.textContent = this.__displayName;

    return {
      element: link,
    };
  }

  constructor(
    id: string,
    displayName: string,
    mentionType: MentionType,
    key?: NodeKey,
  ) {
    super(key);
    this.__id = id;
    this.__displayName = displayName;
    this.__mentionType = mentionType;
  }

  createDOM(): HTMLElement {
    const container = document.createElement('span');
    return container;
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, _config: EditorConfig): JSX.Element {
    return (
      <Mention
        id={this.__id}
        mentionType={this.__mentionType}
        displayName={this.__displayName}
      />
    );
  }
}

export function $createMentionNode(payload: MentionSuggestion): MentionNode {
  return new MentionNode(payload.id, `@${payload.displayName}`, payload.type);
}

export function $isMentionNode(node: unknown): node is MentionNode {
  return node instanceof MentionNode;
}

// Map type -> link pattern
export function resolveMentionHref(type: MentionType, id: string): string {
  switch (type) {
    case 'MENTOR':
      return getRoutePath(Pages.MENTOR_DETAILS, { mentorId: id });
    case 'FOUNDER':
      return getRoutePath(Pages.FOUNDER_DETAILS, { founderId: id });
    case 'VENTURE':
      return getRoutePath(Pages.VENTURE_DETAILS, { ventureId: id });
    default:
      return Pages.HOME;
  }
}
